import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { fetchSession } from '#/api/auth';
import { getWithTtl, removeWithTtl, setWithTtl } from '#/lib/expiringStorage';
import { queryClient } from '#/lib/queryClient';
import { LOGIN_FLOW_TTL_MS } from '#/config/auth';
import { useCartStore } from '#/stores/cart';

/**
 * 인증 상태 스토어 — 인증 수준 3단계(guest/pending/verified)를 단일 enum 으로 관리.
 *
 * IA(03-정보구조-IA) 권한 노출 모델: 비회원(guest) / 회원 인증대기(pending) / 회원 인증완료(verified).
 *   - 탐색·랜딩·법적고지·마이페이지 = pending 도 진입(본인 격리는 백엔드 책임).
 *   - 결제·다운로드(`requiresVerified`) = verified 만.
 * ⚠️ 클라 가드는 UX 레이어일 뿐 — 실제 강제는 백엔드 @PreAuthorize 검증(IA 03:282).
 *
 * 단일 enum(SSoT)을 두고 boolean view 는 getter 로 파생한다(불가능 조합 배제).
 * 권한(role/permission) 디렉티브는 도입하지 않는다 — FO 는 인증 수준 분기만(MIGRATION_PROMPT §4-2).
 *
 * <b>056 이메일 인증</b>: 로컬 가입은 <b>세션을 받지 않고</b> 인증 안내 화면으로 간다(FR-002). 구글 가입·로그인은
 * 세션을 받아 {@link markSsoLoggedIn}(→verified)로 전이한다.
 *
 * <p>🔴 <b>pending 은 여전히 쓰지 않는다.</b> 미인증 사용자는 세션 자체가 없어 "인증 대기 상태의 로그인"이라는 것이
 * 성립하지 않기 때문이다 — 인증 안내 화면은 라우터 state 로 대상을 받으므로 store 상태가 필요 없다. 즉
 * <b>세션을 가진 사용자는 정의상 인증을 마친 사용자</b>이고, 이 불변식 덕분에 {@link restoreSession} 이
 * "인증됐으면 verified" 로 승격해도 항상 옳다(research D8).
 *
 * <p>이 불변식은 "미인증은 로그인 차단"이라는 선택에 의존한다 — 미인증 세션을 허용하는 방향으로 정책이 바뀌면
 * 여기가 가장 먼저 깨지고, 그때는 백엔드 {@code SessionInfo} 에 인증 수준 필드를 추가해야 한다.
 * pending 값·{@link login} 은 그 대비로 남겨 둔다.
 */
export type AuthLevel = 'guest' | 'pending' | 'verified';

/**
 * 로그인 후 복귀지(returnUrl) sessionStorage 키 — 로그인 유도 직전 화면 경로를 보존한다.
 * 로컬 로그인은 `/login?redirect=` 쿼리로 충분하지만, 구글 SSO 는 백엔드 OAuth 왕복(브라우저 전체
 * 리다이렉트)이라 쿼리가 콜백까지 살아남지 못한다. 양 경로가 "복귀지"라는 동일 개념을 한 곳에서 공유하도록
 * sessionStorage 를 단일 매개로 둔다 — 콜백·로그인 화면은 특정 기능(담기 등)을 몰라도 복귀를 처리한다.
 * TTL 봉투로 저장(LOGIN_FLOW_TTL_MS) — 미로그인 이탈 시 잔존을 자동 폐기한다([[expiringStorage]]).
 */
const RETURN_URL_KEY = 'fo.auth.returnUrl';

/**
 * 복귀지 안전성 검증 — open-redirect 방어. 앱 내부 절대경로만 허용한다.
 * sessionStorage 출처라 외부 주입 가능성은 낮으나, returnUrl 은 곧 router 이동 대상이므로 방어적으로 막는다.
 * 허용: '/' 로 시작하는 내부 경로. 차단: 빈 값·절대 URL('http://')·프로토콜 상대('//evil.com') 및 그
 * 백슬래시 변형('/\evil.com' — 브라우저가 '//' 로 정규화해 우회 가능). 두 번째 문자가 슬래시/백슬래시면 차단.
 */
function isSafeReturnUrl(url: string | null): url is string {
  return typeof url === 'string' && url.startsWith('/') && url[1] !== '/' && url[1] !== '\\';
}

export const useAuthStore = defineStore('auth', () => {
  /** 인증 수준(단일 SSoT). 초기값 guest. */
  const authLevel = ref<AuthLevel>('guest');

  /**
   * 로그인 사용자 이메일 — 세션 복원(fetchSession) 시 채워진다. 사용자별 로컬 상태
   * (예: 배너 닫음 localStorage 키 스코프)에 쓰는 표시·식별 보조값이다. 같은 세션에서
   * SPA 로그인 직후에는 null 일 수 있다(다음 부팅 복원 시 채워짐) — 소비처는 폴백을 둔다.
   */
  const email = ref<string | null>(null);

  /**
   * 로그인 후 복귀지(returnUrl) — 보호 동작에서 로그인으로 유도하기 직전, 돌아올 경로를 보존한다.
   * sessionStorage 미러로 SSO 전체 리다이렉트에서도 생존. 기능 비종속(담기·다운로드 등 무엇이든 공용).
   */
  const returnUrl = ref<string | null>(readReturnUrl());

  /** sessionStorage(TTL 봉투)에서 안전한 복귀지를 읽는다(없거나·만료·안전하지 않으면 null). */
  function readReturnUrl(): string | null {
    const raw = getWithTtl<string>(RETURN_URL_KEY, LOGIN_FLOW_TTL_MS);
    return isSafeReturnUrl(raw) ? raw : null;
  }

  /**
   * 복귀지 설정 — 보호 동작에서 로그인으로 유도하기 직전 호출. 안전한 내부 경로만 보존한다.
   * @param url 로그인 완료 후 돌아갈 앱 내부 경로(예: '/materials').
   */
  function setReturnUrl(url: string): void {
    if (!isSafeReturnUrl(url)) return;
    returnUrl.value = url;
    setWithTtl(RETURN_URL_KEY, url);
  }

  /**
   * 복귀지 소비 — 로그인 완료 후 1회 읽고 즉시 비운다(중복 복귀 방지). 로컬·SSO 양 경로가 공용으로 호출한다.
   * @returns 보존된 복귀지, 없으면 null(호출부가 기본 경로 '/' 로 대체).
   */
  function consumeReturnUrl(): string | null {
    const url = returnUrl.value;
    returnUrl.value = null;
    removeWithTtl(RETURN_URL_KEY);
    return url;
  }

  /**
   * 부팅 세션 복원 완료 여부. 메모리 상태는 새로고침마다 guest 로 초기화되므로, 영속 쿠키로 1회 복원한다.
   * 가드가 보호 라우트 판정 전 restoreSession() 의 완료를 보장하는 데 쓴다(중복 호출 방지).
   */
  const sessionRestored = ref(false);
  /** 진행 중 복원 Promise(동시 진입 시 단일 호출 공유). */
  let restorePromise: Promise<void> | null = null;

  /**
   * @deprecated 장바구니 수량은 useCartStore 가 SSoT. 헤더는 cart.count 를 직독한다.
   * 잔존 호환용 — 신규 코드에서 사용 금지(다음 정리 시 제거).
   */
  const cartCount = ref(0);

  /** 로그인 여부(pending|verified) — AppHeader 메뉴 노출 분기. */
  const isAuthenticated = computed(() => authLevel.value !== 'guest');
  /** 메일 인증 완료(verified) — 결제·다운로드 게이팅. */
  const isVerified = computed(() => authLevel.value === 'verified');
  /** 현재 인증 수준(guest|pending|verified). */
  const authState = computed<AuthLevel>(() => authLevel.value);

  /** 저수준 세터 — 실연동 시 서버 응답 레벨을 직접 반영. */
  function setAuthLevel(level: AuthLevel): void {
    authLevel.value = level;
  }

  /**
   * 로그인 성공 → 인증대기(pending). 메일 인증 완료 전 단계.
   *
   * @deprecated 056 에서도 호출처가 없다 — 미인증 사용자는 세션 자체를 받지 못하므로(가입 시 미발급·로그인 차단)
   *     "pending 상태로 로그인된" 사용자가 존재하지 않는다. 향후 미인증 세션을 허용하는 정책으로 바뀌면 그때 되살린다
   *     (클래스 javadoc 의 불변식 참조).
   */
  function login(): void {
    authLevel.value = 'pending';
  }

  /** 메일 인증 완료 → verified. */
  function verify(): void {
    authLevel.value = 'verified';
  }

  /**
   * 로그아웃 → guest. <b>로컬 클라이언트 상태 정리의 단일 지점(SSoT)</b>.
   *
   * <p>헤더 [로그아웃] 버튼과 http.ts 401 인터셉터({@code clearSession})가 모두 이 함수로 수렴하므로,
   * 사용자 전환 시 비워야 할 모든 로컬 상태를 여기서 한 번에 정리한다 — 어느 한 경로만 정리하면
   * 다른 경로에서 이전 사용자 데이터가 새는 누락이 생긴다.
   *  - 인증 수준 → guest, 미완 복귀지·보류 담기 의도 폐기(다음 로그인 흐름 오염 방지).
   *  - 장바구니 비우기(cart 스토어) — 헤더 버튼만 비우던 것을 401 경로까지 일관화.
   *  - vue-query 캐시 전체 무효화 — 사용자별 캐시(보유 grant 등)가 다음 사용자에게 누출되지 않도록.
   *    로컬 로그인은 SPA(리로드 없음)라 캐시가 살아남으므로 명시적 정리가 필수.
   */
  function logout(): void {
    authLevel.value = 'guest';
    email.value = null;
    cartCount.value = 0;
    // 미완 복귀지를 폐기 — 다음 로그인 흐름에 이전 의도가 새지 않도록(보안·정합성).
    consumeReturnUrl();
    // 장바구니·보류 담기 의도 폐기(이전 세션 선택이 다음 사용자에게 남지 않도록).
    useCartStore().clear();
    // 사용자별 vue-query 캐시(보유 grant 등) 전체 무효화 — 교차계정 누출 차단.
    queryClient.clear();
    // 부팅 복원 플래그를 내려, 다음 보호 라우트 진입 시 가드가 쿠키 세션을 재확인하도록 한다.
    // (미초기화 시 markSsoLoggedIn 이 올린 true 가 남아 restoreSession 이 스킵되고 store↔쿠키 상태가 어긋남)
    sessionRestored.value = false;
  }

  /**
   * 구글 SSO 로그인 성공 처리 → verified.
   * 구글 계정은 이미 이메일 인증이 완료된 상태이므로 pending 단계를 건너뛰고
   * 바로 verified 로 승격한다(메일 인증 불필요).
   * 세션 실체는 백엔드가 발급한 HttpOnly 쿠키(IPSI_SESSION) — 여기서는 클라이언트 상태만 갱신.
   */
  function markSsoLoggedIn(): void {
    authLevel.value = 'verified';
    // 콜백이 직접 로그인 상태를 확정했으므로 부팅 복원을 다시 돌릴 필요가 없다(중복 me 호출 방지).
    sessionRestored.value = true;
    // 사용자별 로컬 상태 키(배너 닫음 등)의 스코프용 이메일을 채운다 — restoreSession 이 스킵되는
    // 경로(SPA 로그인·가입·SSO 콜백)에서는 여기가 유일한 충전 지점이다. 실패해도 로그인 흐름엔 무영향
    // (소비처가 email 미확정 상태를 스스로 방어한다).
    if (!email.value) {
      void fetchSession().then((session) => {
        if (session?.authenticated) {
          email.value = session.email ?? null;
        }
      });
    }
  }

  /**
   * 세션 만료·401 응답 → 클라이언트 인증 상태를 guest 로 초기화.
   * 쿠키 자체는 백엔드가 관리하므로 프론트는 로컬 상태만 정리한다.
   * http.ts 401 핸들러에서 호출 — 직접 호출 시에도 logout() 과 동일 결과.
   * ⚠️ logout() 이 returnUrl 을 폐기하므로, 401 핸들러는 이 호출 **뒤에** 만료 시점의
   * 현재 화면을 setReturnUrl 로 다시 보존한다(재로그인 후 보던 화면 복귀 — 순서 계약).
   */
  function clearSession(): void {
    logout();
  }

  /**
   * 부팅 시 영속 쿠키(IPSI_SESSION)로 로그인 상태를 복원한다(FR-017·SC-011).
   *
   * 새로고침·탭 재오픈 후 메모리 authLevel 이 guest 로 초기화되므로, 라우터 가드가 보호 라우트를 판정하기 전에
   * 1회 호출해 서버에 세션 유효성을 확인한다. 유효하면 verified(구글 SSO 는 이메일 인증 완료 → pending 건너뜀),
   * 미로그인이면 guest 유지. 한 번 완료되면 이후 호출은 즉시 반환(동시 진입은 단일 Promise 공유).
   */
  async function restoreSession(): Promise<void> {
    if (sessionRestored.value) return;
    if (restorePromise) return restorePromise;
    restorePromise = (async () => {
      const session = await fetchSession();
      if (session?.authenticated) {
        authLevel.value = 'verified';
        email.value = session.email ?? null;
      }
      sessionRestored.value = true;
    })();
    try {
      await restorePromise;
    } finally {
      restorePromise = null;
    }
  }

  /** @deprecated useCartStore 사용. */
  function setCartCount(count: number): void {
    cartCount.value = Math.max(0, count);
  }

  return {
    authLevel,
    email,
    sessionRestored,
    returnUrl,
    cartCount,
    isAuthenticated,
    isVerified,
    authState,
    setAuthLevel,
    setReturnUrl,
    consumeReturnUrl,
    login,
    verify,
    logout,
    markSsoLoggedIn,
    clearSession,
    restoreSession,
    setCartCount,
  };
});
